import * as THREE from 'three';
import { elStage } from '../platform/dom';
import { renderer } from '../platform/renderer';
import { resizePostFX } from './postfx';
import { onQuality } from '../platform/quality';
/* ---------- Three 基礎 ---------- */
export var scene = new THREE.Scene();
export var camera = new THREE.PerspectiveCamera(50, 1, 0.5, 400);
export var CAM_OFF = new THREE.Vector3(0, 33, 23);

/* 半球光從 0.85 降到 0.72（v2 §1.3b）——
   **緣光要有效，暗部必須夠暗**。 */
export var hemi = new THREE.HemisphereLight(0x8fb6ff, 0x151a26, 0.72); scene.add(hemi);
export var sun = new THREE.DirectionalLight(0xdfe9ff, 0.95); sun.position.set(24, 46, 18); scene.add(sun);
sun.castShadow = true;
/* ---------- 陰影相機的框：**要罩得住整個看得到的地面** ----------

   歷史：±4.5（只罩機體）→ ±15（罩得住附近的 Boss）→ **±64（罩得住可見地面）**。

   使用者 2026-08-04：「建築物的陰影不應該跟著玩家位置移動而變動」。
   真因就是這個框：它綁在玩家身上（`sunFollow()`），±15 的框只有
   30 m 見方，而遊戲鏡頭一次看得到約 70 m 的地面 —— 於是建築走進框裡
   才有影子、走出去影子就消失，玩家移動時整片建築的影子在生滅。
   **靜態物體的影子必須是靜態的**，否則它讀成閃爍的髒污而不是光影。

   ±64 是**量出來的**，不是估的：把螢幕四角打到地面（y=0）再投進陰影相機的
   裁剪空間，±46 時左上角落在 1.35（框外），±64 才五個取樣點全部 inside。
   驗證腳本見 commit 訊息；改動框的大小時要重跑一次，不要用眼睛判斷。

   代價是 texel 從 1.5 cm 變成 6.3 cm —— 但在遊戲鏡頭下 1 m 約 13 px，
   一個 texel 約 0.8 px，仍然是次像素等級，機體的影子不會變糊。

   ⚠️ `near`／`far` 要跟著框一起改：光源在目標點外 58 單位，
   半徑 64 的球體在光軸上的投影是 [-6, 122]，所以 near 要小、far 要大。
   正交投影的深度是線性的，拉寬不會有精度問題。
   忘了改的話遠處的建築會被近平面切掉 —— 症狀跟「框太小」一模一樣。

   ⚠️ `mapSize` 是**畫質設定的旋鈕之一**：低階機器會降回 1024²，
   所以不要在別處假設它是 2048。 */
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.camera.left = -64; sun.shadow.camera.right = 64;
sun.shadow.camera.top = 64; sun.shadow.camera.bottom = -64;
sun.shadow.camera.near = 2; sun.shadow.camera.far = 140;
/* `bias` 從 -0.0012 收到 -0.0005，改用 `normalBias` 擋痤瘡。
   兩者的差別是**這一輪影子從「浮起來」變回「貼著腳」的關鍵**：
   `bias` 是沿深度方向整片位移，開大會讓影子與腳底脫節（peter-panning）；
   `normalBias` 是沿法線把取樣點推進物體內部，對曲面與斜面有效而且不脫節。
   ⚠️ 框放大四倍之後 texel 也大四倍，痤瘡的風險跟著放大 ——
   所以 `normalBias` 一起從 0.035 提到 0.07。 */
sun.shadow.bias = -0.0005;
sun.shadow.normalBias = 0.07;
/* 陰影圖邊長是畫質設定的旋鈕之一。換邊長要**把舊的 shadow map 丟掉**，
   否則 three 會繼續用已經配置好的那一張，設定看起來沒生效。 */
onQuality(function (q) {
  if (sun.shadow.mapSize.x === q.shadowMap) return;
  sun.shadow.mapSize.set(q.shadowMap, q.shadowMap);
  if (sun.shadow.map) { (sun.shadow.map as any).dispose(); sun.shadow.map = null as any; }
});
scene.add(sun.target);

var SUN_DIR = new THREE.Vector3(24, 46, 18).normalize();
/** 換主光方向。移植進來的世界自帶低角度的夕陽（原型 §4），
    照原本這個高角度打下去會變成正午的平光 —— 建築沒有側面、
    地面整片過曝，而那正是「看起來像玩具」的來源。 */
export function setSunDir(x: number, y: number, z: number) {
  SUN_DIR.set(x, y, z).normalize();
}
/** 讓陰影相機跟著目標點。每幀呼叫一次。 */
export function sunFollow(x, z) {
  sun.target.position.set(x, 0, z);
  sun.position.set(x + SUN_DIR.x * 58, SUN_DIR.y * 58, z + SUN_DIR.z * 58);
  sun.target.updateMatrixWorld();
}
/* **雙側緣光**（v2 §1.3b）。v1 是單盞暖 rim，只鑲得亮一邊的肩線。
   兩盞都放在**後方**左右夾住：兩側輪廓同時被亮邊描出來，
   而暗部不會被填平（正面沒有補光）。 */
export var rim = new THREE.DirectionalLight(0xff8a4a, 0.50); rim.position.set(-30, 16, -26); scene.add(rim);
export var rim2 = new THREE.DirectionalLight(0x6fa8ff, 0.42); rim2.position.set(28, 13, -30); scene.add(rim2);

export function resize() {
  var w = elStage.clientWidth || 1, h = elStage.clientHeight || 1;
  if (renderer) renderer.setSize(w, h, false);
  camera.aspect = w / h; camera.updateProjectionMatrix();
  resizePostFX();
}

export var G = {
  box: new THREE.BoxGeometry(1, 1, 1),
  cyl: new THREE.CylinderGeometry(0.5, 0.5, 1, 10),
  cone: new THREE.ConeGeometry(0.5, 1, 8),   // demo 是 8 段，7 段的稜線落點不同
  sph: new THREE.SphereGeometry(0.5, 10, 8),
  oct: new THREE.OctahedronGeometry(0.5, 0),
  ico: new THREE.IcosahedronGeometry(0.5, 0),
  torus: new THREE.TorusGeometry(0.5, 0.085, 5, 14),
  /* 倒角塊專用：**8 段 + thetaStart = π/8**，讓四個平面對齊 ±X/±Z、
     只剩四條稜是倒角。仍然是 CylinderGeometry，沒有新增第六種 primitive。

     為什麼不能用「把 cyl 轉 45°」：8 段時 π/4 剛好等於一整個 segment，
     等寬旋轉等於沒做；10 段時怎麼轉都讀成圓柱。而且旋轉寫在 mesh 上會
     踩到矩陣順序（T·R·S，scale 先作用再被轉），非等寬的截面會整個轉斜。
     用 thetaStart 一次解決兩個問題。 */
  bev: new THREE.CylinderGeometry(0.5, 0.5, 1, 8, 1, false, Math.PI / 8),
  // 機體專用的環：比 `torus` 粗且平滑，讀得出是機削件不是橡皮圈
  tor: new THREE.TorusGeometry(0.40, 0.10, 8, 18),
  ring: new THREE.RingGeometry(0.86, 1, 40),
  circle: new THREE.CircleGeometry(1, 18)
};
/** 8 邊柱的頂點在半徑 0.5 上，量到的是**角**不是**面** ——
    乘這個係數，`bevel()` 的 w/d 才會跟同名的 box 一樣寬。 */
export var K_BEV = 1 / Math.cos(Math.PI / 8);   // 1.0824

export var MAT_SHADOW = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.3, depthWrite: false });
export var WHITE = new THREE.Color(0xffffff);

export function mesh(geo, mat, s, p?, r?) {
  var m = new THREE.Mesh(geo, mat);
  if (s) m.scale.set(s[0], s[1], s[2]);
  if (p) m.position.set(p[0], p[1], p[2]);
  if (r) m.rotation.set(r[0], r[1], r[2]);
  return m;
}
export function blob(radius) {
  var m = new THREE.Mesh(G.circle, MAT_SHADOW);
  m.rotation.x = -Math.PI / 2; m.position.y = 0.035; m.scale.set(radius, radius, radius);
  return m;
}
export function stdMat(color, opt) {
  opt = opt || {};
  var m = new THREE.MeshStandardMaterial({
    color: color, metalness: opt.metal === undefined ? 0.35 : opt.metal,
    roughness: opt.rough === undefined ? 0.52 : opt.rough,
    emissive: new THREE.Color(opt.em || 0x000000),
    emissiveIntensity: opt.ei === undefined ? 1 : opt.ei, flatShading: !!opt.flat,
    /* `vcol`：開 `vertexColors`，給走 `instanceColor` 的敵人材質用。
       **開了之後幾何一定要有 `color` 屬性**，否則 WebGL 餵 (0,0,0) 整台變全黑
       （`entities/enemy-parts.ts` 的 `withColor` 就是為了這件事）。 */
    vertexColors: !!opt.vcol
  });
  m.userData.baseEm = new THREE.Color(opt.em || 0x000000);
  m.userData.baseEi = opt.ei === undefined ? 1 : opt.ei;
  return m;
}
export function Pool(size, factory) {
  this.items = []; this.cursor = 0;
  for (var i = 0; i < size; i++) { var o = factory(i); o.alive = false; this.items.push(o); }
}
/* 只給效能面板用。O(n) 掃描，所以呼叫端要自己確認 perf.enabled 才呼叫。 */
Pool.prototype.countAlive = function () {
  var n = 0;
  for (var i = 0; i < this.items.length; i++) if (this.items[i].alive) n++;
  return n;
};
Pool.prototype.get = function () {
  var n = this.items.length;
  for (var i = 0; i < n; i++) {
    var idx = (this.cursor + i) % n;
    if (!this.items[idx].alive) { this.cursor = (idx + 1) % n; this.items[idx].alive = true; return this.items[idx]; }
  }
  return null;
};
